import React, { useState, useEffect } from "react";
import { formatLocalDateTime } from "./utils/dates";
import axios from "axios";

// Create axios instance with environment-based config
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? "/api"
    : "http://localhost:8000");

const axiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  validateStatus: function (status) {
    return status >= 200 && status < 500; // Don't reject if status is not 2xx
  },
});

function ProfileTracking() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [profileUrl, setProfileUrl] = useState("");
  const [matchId, setMatchId] = useState("");
  const [addingProfile, setAddingProfile] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [expandedProfiles, setExpandedProfiles] = useState(new Set());
  const [showMatchIdHelp, setShowMatchIdHelp] = useState(false);
  const [loggedInUserSteamId, setLoggedInUserSteamId] = useState(null); // New state for logged-in user's Steam ID

  useEffect(() => {
    loadTrackedProfiles();
    loadAvailableTags();
  }, []);

  async function loadTrackedProfiles() {
    try {
      const res = await axiosInstance.get('/profiles/tracked');
      if (res.status === 401) {
        setError("Session expired. Please log out and log in again.");
        return;
      }
      setProfiles(res.data.profiles || []);
      setLoggedInUserSteamId(res.data.user_steam_id); // Set the logged-in user's Steam ID
    } catch (e) {
      console.error("Failed to load tracked profiles", e);
      const errorMessage = e.response?.data?.error || "Failed to load tracked profiles";
      const errorDetails = e.response?.data?.details || "";
      setError(errorDetails ? `${errorMessage}\n\n${errorDetails}` : errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function loadAvailableTags() {
    try {
      const res = await axiosInstance.get('/profiles/tags/available');
      setAvailableTags(res.data.tags || []);
    } catch (e) {
      console.error("Failed to load available tags", e);
    }
  }

  async function addProfile() {
    if (!profileUrl.trim()) {
      setError("Please enter a Steam profile URL");
      return;
    }

    setAddingProfile(true);
    setError(null);

    try {
      const res = await axiosInstance.post('/profiles/add', {
        profile_url: profileUrl.trim(),
        tags: selectedTags,
        match_id: matchId.trim()
      });

      setSuccess(res.data.message);
      setProfileUrl("");
      setMatchId("");
      setSelectedTags([]);
      await loadTrackedProfiles(); // Refresh the list
    } catch (e) {
      console.error("Failed to add profile", e);
      const errorMessage = e.response?.data?.error || "Failed to add profile";
      const errorDetails = e.response?.data?.details || "";
      setError(errorDetails ? `${errorMessage}\n\n${errorDetails}` : errorMessage);
    } finally {
      setAddingProfile(false);
    }
  }

  async function removeProfile(steamId) {
    try {
      await axiosInstance.delete(`/profiles/${steamId}`);
      setSuccess("Profile removed from tracking");
      await loadTrackedProfiles(); // Refresh the list
    } catch (e) {
      console.error("Failed to remove profile", e);
      setError("Failed to remove profile");
    }
  }

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const toggleProfileExpansion = (steamId) => {
    setExpandedProfiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(steamId)) {
        newSet.delete(steamId);
      } else {
        newSet.add(steamId);
      }
      return newSet;
    });
  };

  async function addMatchIdToProfile(steamId, matchId) {
    if (!matchId.trim()) {
      setError("Please enter a match ID");
      return;
    }

    try {
      const res = await axiosInstance.post(`/profiles/${steamId}/match`, {
        match_id: matchId.trim()
      });

      setSuccess(res.data.message);
      await loadTrackedProfiles(); // Refresh the list
    } catch (e) {
      console.error("Failed to add match ID", e);
      const errorMessage = e.response?.data?.error || "Failed to add match ID";
      const errorDetails = e.response?.data?.details || "";
      setError(errorDetails ? `${errorMessage}\n\n${errorDetails}` : errorMessage);
    }
  }

  const TagButton = ({ tag, isSelected, onClick }) => (
    <button
      onClick={onClick}
      style={{
        padding: "4px 8px",
        margin: "2px",
        borderRadius: "12px",
        border: "1px solid #ddd",
        backgroundColor: isSelected ? "#007bff" : "#f8f9fa",
        color: isSelected ? "white" : "#333",
        cursor: "pointer",
        fontSize: "12px",
        transition: "all 0.2s"
      }}
    >
      {tag}
    </button>
  );

  const ProfileCard = ({ profile }) => {
    const isExpanded = expandedProfiles.has(profile.steam_id);
    const [showRawBanData, setShowRawBanData] = useState(false);
    
    const getStatusColor = (status) => {
      switch (status) {
        case "Banned": return "#dc3545";
        case "Suspicious": return "#ffc107";
        case "Clean": return "#28a745";
        default: return "#6c757d";
      }
    };

    const getBanInfo = () => {
      if (profile.vac_banned && profile.game_banned) {
        return "VAC + Game Banned";
      } else if (profile.vac_banned) {
        return "VAC Banned";
      } else if (profile.game_banned) {
        return "Game Banned";
      }
      return "No Bans";
    };

    return (
      <div style={{
        border: "1px solid #ddd",
        borderRadius: "8px",
        marginBottom: "10px",
        backgroundColor: "#fff",
        overflow: "hidden"
      }}>
        {/* Collapsed View - Always Visible */}
        <div
          onClick={() => toggleProfileExpansion(profile.steam_id)}
          style={{
            padding: "12px 15px",
            cursor: "pointer",
            backgroundColor: (profile.vac_banned || profile.game_banned) ? "#ffebeb" : "#fff", // Light red if banned
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: isExpanded ? "1px solid #ddd" : "none"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src={profile.avatar}
              alt="Avatar"
              style={{ width: "32px", height: "32px", borderRadius: "50%" }}
            />
            <div>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                {profile.nickname}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                Added on {new Date(profile.date_added).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {((profile.vac_banned || profile.game_banned) && profile.is_newly_banned) && (
                  <>
                    <strong style={{ fontWeight: "900", margin: "0 3px" }}> | </strong>
                    <span style={{ fontWeight: "bold", color: "#dc3545" }}>
                      {'Banned on '}
                      {(() => {
                        let latestBanDate = null;
                        if (profile.vac_ban_date) {
                          latestBanDate = new Date(profile.vac_ban_date);
                        }
                        if (profile.game_ban_date) {
                          const gameBanDt = new Date(profile.game_ban_date);
                          if (!latestBanDate || gameBanDt > latestBanDate) {
                            latestBanDate = gameBanDt;
                          }
                        }
                        return latestBanDate ? latestBanDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                      })()}
                    </span>
                  </>
                )}
                {((profile.vac_banned || profile.game_banned) && profile.had_bans_before_tracking && !profile.is_newly_banned) && (
                  <>
                    <strong style={{ fontWeight: "900", margin: "0 3px" }}> | </strong>
                    <span style={{ fontWeight: "bold", color: "#ff9800" }}>
                      {"Has old VAC/Game bans"}
                      {(() => {
                        let latestBanDate = null;
                        if (profile.vac_ban_date) {
                          latestBanDate = new Date(profile.vac_ban_date);
                        }
                        if (profile.game_ban_date) {
                          const gameBanDt = new Date(profile.game_ban_date);
                          if (!latestBanDate || gameBanDt > latestBanDate) {
                            latestBanDate = gameBanDt;
                          }
                        }
                        return latestBanDate ? ` (last ban: ${latestBanDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})` : '';
                      })()}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
              {/* Tags */}
              {profile.tags && profile.tags.length > 0 && (
                <div style={{ display: "flex", gap: "4px" }}>
                  {profile.tags.map(tag => (
                    <span key={tag} style={{
                      padding: "2px 6px",
                      backgroundColor: "#e9ecef",
                      color: "#495057",
                      borderRadius: "8px",
                      fontSize: "10px",
                      fontWeight: "bold"
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Status */}
              <span style={{
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "11px",
                color: "white",
                backgroundColor: getStatusColor(profile.is_newly_banned ? "Banned" : (profile.had_bans_before_tracking ? "Clean" : profile.current_status))
              }}>
                {profile.is_newly_banned ? "Banned" : (profile.had_bans_before_tracking ? "Clean" : profile.current_status)}
              </span>

              
              {/* Expand/Collapse Icon */}
              <span style={{ fontSize: "16px", color: "#666" }}>
                {isExpanded ? "▼" : "▶"}
              </span>
            </div>
            {(profile.vac_banned || profile.game_banned) && (
              <div style={{ 
                fontSize: "12px", 
                color: "#dc3545", 
                fontWeight: "bold", 
                marginTop: "2px" 
              }}>
                {/* Removed: Banned on {profile.vac_ban_date && profile.game_ban_date ? `${new Date(profile.vac_ban_date).toLocaleDateString()} / ${new Date(profile.game_ban_date).toLocaleDateString()}` : (profile.vac_ban_date ? new Date(profile.vac_ban_date).toLocaleDateString() : new Date(profile.game_ban_date).toLocaleDateString())} */}
              </div>
            )}
          </div>
        </div>

        {/* Expanded View */}
        {isExpanded && (
          <div style={{ padding: "15px", backgroundColor: "#f8f9fa" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              {/* Profile Info */}
              <div>
                <h4 style={{ marginBottom: "10px", color: "#333" }}>Profile Details</h4>
                <div style={{ fontSize: "14px" }}>
                  <div><strong>Steam ID:</strong> <a href={`http://steamcommunity.com/profiles/${profile.steam_id}`} target="_blank" rel="noopener noreferrer" style={{ color: "#007bff", textDecoration: "underline" }}>{profile.steam_id}</a></div>
                  <div><strong>Nickname:</strong> {profile.nickname}</div>
                  <div><strong>Status:</strong> {profile.current_status}</div>
                  <div><strong>Ban Status:</strong> {getBanInfo()}</div>
                  <div><strong>CS2 Playtime:</strong> {profile.cs2_playtime} hours</div>
                  <div><strong>Tracked by:</strong> {profile.tracked_by_count} users</div>
                  <div><strong>Last Checked:</strong> {formatLocalDateTime(profile.last_checked)}</div>
                  <div><strong>Next Check:</strong> {formatLocalDateTime(profile.next_check)}</div>
                </div>
              </div>

              {/* Ban Details */}
              <div>
                <h4 style={{ marginBottom: "10px", color: "#333" }}>Ban Information</h4>
                <div style={{ fontSize: "14px" }}>
                  <div><strong>VAC Banned:</strong> {profile.vac_banned ? "Yes" : "No"}</div>
                  <div><strong>Game Banned:</strong> {profile.game_banned ? "Yes" : "No"}</div>
                  <div><strong>Total Bans:</strong> {profile.total_bans}</div>
                  {profile.vac_ban_date && (
                    <div><strong>VAC Ban Date:</strong> {new Date(profile.vac_ban_date).toLocaleDateString()}</div>
                  )}
                  {profile.game_ban_date && (
                    <div><strong>Game Ban Date:</strong> {new Date(profile.game_ban_date).toLocaleDateString()}</div>
                  )}
                  {profile.days_since_last_ban !== null && (
                    <div><strong>Days Since Last Ban:</strong> {profile.days_since_last_ban}</div>
                  )}
                  <div><strong>Profile Visibility:</strong> {profile.profile_visibility}</div>
                </div>
              </div>
            </div>

            {/* Tags Section */}
            {profile.tags && profile.tags.length > 0 && (
              <div style={{ marginTop: "15px" }}>
                <h4 style={{ marginBottom: "10px", color: "#333" }}>Cheater Tags</h4>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {profile.tags.map(tag => (
                    <span key={tag} style={{
                      padding: "6px 12px",
                      backgroundColor: "#007bff",
                      color: "white",
                      borderRadius: "16px",
                      fontSize: "12px",
                      fontWeight: "bold"
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Match ID Section */}
            <div style={{ marginTop: "15px" }}>
              
              {profile.match_id ? (
                <div style={{ fontSize: "14px" }}>
                  <div><strong>Match ID:</strong> <a href={`steam://rungame/730/${loggedInUserSteamId}/+csgo_download_match%20${profile.match_id}`} target="_blank" rel="noopener noreferrer" style={{ color: "#007bff", textDecoration: "underline" }}>{profile.match_id}</a></div>
                  
                </div>
              ) : (
                <div>
                  
                  <div style={{ fontSize: "14px", marginBottom: "10px" }}>
                    <strong>Add Match ID:</strong>
                  </div>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder="CSGO-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                      style={{
                        flex: 1,
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        fontSize: "14px"
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const matchId = e.target.value.trim();
                          if (matchId) {
                            addMatchIdToProfile(profile.steam_id, matchId);
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <button
                      onClick={(e) => {
                        const matchId = e.target.previousSibling.value.trim();
                        if (matchId) {
                          addMatchIdToProfile(profile.steam_id, matchId);
                          e.target.previousSibling.value = '';
                        }
                      }}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px"
                      }}
                    >
                      Add
                    </button>
                  </div>
                  
                </div>
              )}
            </div>

            {/* Remove Button */}
            <div style={{ marginTop: "15px", textAlign: "right" }}>

              {/* Show/Hide Raw Ban Data Button */}
              {profile.full_ban_data && (
                <div style={{ textAlign: "left", marginTop: "15px" }}>
                  <button
                    onClick={() => setShowRawBanData(!showRawBanData)}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      marginBottom: "10px"
                    }}
                  >
                    {showRawBanData ? "Hide Raw Ban Data" : "Show Raw Ban Data"}
                  </button>
                  {showRawBanData && (
                    <pre style={{
                      backgroundColor: "#e9ecef",
                      padding: "10px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all"
                    }}>
                      {JSON.stringify(profile.full_ban_data, null, 2)}
                    </pre>
                  )}
                </div>
              )}

              <button 
                onClick={() => removeProfile(profile.steam_id)}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px"
                }}
              >
                Remove from Tracking
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <p>Loading tracked profiles...</p>;

  return (
    <div style={{ maxWidth: "800px", margin: "30px auto", fontFamily: "Arial, sans-serif" }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "20px"
      }}>
        <h2 style={{ margin: 0 }}>Followed Accounts ({profiles.length})</h2>
        <button 
          onClick={() => setShowAddForm(!showAddForm)}
          style={{
            padding: "10px 20px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <span>{showAddForm ? "Cancel" : "Add Account"}</span>
          <span style={{ fontSize: "16px" }}>{showAddForm ? "×" : "+"}</span>
        </button>
      </div>
      
      {error && (
        <div style={{ 
          padding: "10px", 
          backgroundColor: "#f8d7da", 
          color: "#721c24", 
          borderRadius: "4px",
          marginBottom: "15px",
          whiteSpace: "pre-line"
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          padding: "10px", 
          backgroundColor: "#d4edda", 
          color: "#155724", 
          borderRadius: "4px",
          marginBottom: "15px"
        }}>
          {success}
        </div>
      )}

      {/* Add Profile Section - Collapsible */}
      {showAddForm && (
        <div style={{ 
          padding: "20px", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #e9ecef"
        }}>
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Steam Profile URL:
            </label>
            <input
              type="text"
              value={profileUrl}
              onChange={(e) => setProfileUrl(e.target.value)}
              placeholder="https://steamcommunity.com/profiles/123456789"
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "1px solid #ddd",
                fontSize: "14px"
              }}
            />
          </div>

          {/* Match ID Input */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Match ID (optional):
            </label>
            <input
              type="text"
              value={matchId}
              onChange={(e) => setMatchId(e.target.value)}
              placeholder="CSGO-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "1px solid #ddd",
                fontSize: "14px"
              }}
            />
            <div 
              onClick={() => setShowMatchIdHelp(!showMatchIdHelp)}
              style={{ 
                fontSize: "12px", 
                color: "#2196f3", 
                marginTop: "5px",
                cursor: "pointer",
                textDecoration: "underline"
              }}
            >
              How to get Match ID?
            </div>
            {showMatchIdHelp && (
              <div style={{ 
                fontSize: "12px", 
                color: "#666", 
                marginTop: "5px",
                padding: "8px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #e9ecef"
              }}>
                Go to Watch tab, select match and click "Copy Match ID" in the bottom right corner.
              </div>
            )}
          </div>

          {/* Tags Selection */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Tags:
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {availableTags.map(tag => (
                <TagButton
                  key={tag}
                  tag={tag}
                  isSelected={selectedTags.includes(tag)}
                  onClick={() => toggleTag(tag)}
                />
              ))}
            </div>
            {selectedTags.length > 0 && (
              <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                Selected: {selectedTags.join(", ")}
              </div>
            )}
          </div>
          
          <button 
            onClick={addProfile}
            disabled={addingProfile}
            style={{
              padding: "10px 20px",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: addingProfile ? "not-allowed" : "pointer",
              opacity: addingProfile ? 0.6 : 1
            }}
          >
            {addingProfile ? "Adding..." : "Add Profile"}
          </button>
        </div>
      )}

      {/* Tracked Profiles Section */}
      <div>
        
        {profiles.length === 0 ? (
          <div style={{ 
            padding: "20px", 
            backgroundColor: "#f5f5f5", 
            borderRadius: "8px",
            textAlign: "center",
            color: "#666"
          }}>
            No profiles tracked yet. Click "Add Account for Tracking" to start tracking.
          </div>
        ) : (
          <div>
            {[...profiles]
              .sort((a, b) => new Date(b.date_added) - new Date(a.date_added))
              .map((profile) => (
                <ProfileCard key={profile.steam_id} profile={profile} />
              ))
            }
          </div>
        )}
      </div>

      <button 
        onClick={() => window.history.back()} 
        style={{ 
          marginTop: "20px",
          padding: "8px 16px",
          backgroundColor: "#6c757d",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer"
        }}
      >
        Go Back
      </button>
    </div>
  );
}

export default ProfileTracking; 